import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import api, { apiEndpoints, MutationKeys } from '../../../services/axios';
import { ScrubAddressInput, ScrubbedAddress, UseScrubAddressResult } from './types';
import { selectAddressLine2, serviceabilityActions } from '@buy-viasat/redux/src/serviceability';
import {
  normalizeScrubbedAddressLines,
  resolveFiberMduAddress,
  withNormalizedLine1,
} from '@buy-viasat/redux/src/address';
import { appActions, Modals } from '@buy-viasat/redux/src/app';
import { TrackingActions, TrackingCategory } from '@buy-viasat/redux/src/analytics';

export function useScrubAddress<T>(): UseScrubAddressResult<T> {
  const dispatch = useDispatch();
  const addressLine2: string = useSelector(selectAddressLine2);

  const {
    data: scrubAddressData,
    mutateAsync: scrubAddressAsync,
    isPending: loading,
  } = useMutation<ScrubbedAddress, Error, ScrubAddressInput['address']>({
    mutationKey: [MutationKeys.scrubAddress],
    mutationFn: async function mutateScrubAddress(payload: ScrubAddressInput['address']) {
      const response = await api.post(apiEndpoints.scrubAddress, payload);
      return response.data;
    },
    onSuccess: (data: ScrubbedAddress) => {
      const { isAddressInvalid, ...rest } = data;
      // Normalize the per-provider unit shape for the UI only; the raw response is kept below.
      let address = {
        bannedPostalCode: false,
        invalidAddress: isAddressInvalid,
        ...rest,
        addressLines: normalizeScrubbedAddressLines(data.addressLines),
      };

      dispatch(serviceabilityActions.setHasFiberMduAddress(data.hasFiberMduAddress));

      const { needsUnit, shouldNormalizeLine1 } = resolveFiberMduAddress(
        data.hasFiberMduAddress,
        address.addressLines,
        addressLine2,
      );

      // MDU fiber address without an apt/unit number: stop here so the user is prompted
      // to add one before we continue (mirrors scrubServiceAddressSaga).
      if (needsUnit) {
        dispatch(serviceabilityActions.setIsServiceabilityLoading(false));
        return;
      }

      // Keep the exact, unedited addressLines PAL returned so downstream providers
      // (getAvailableProducts / getAddOns) send the same value the backend gave us.
      dispatch(serviceabilityActions.setUnsanitizedAddressLines(data.addressLines));

      // Normalize line 1 with the subunit for the address form display only.
      if (shouldNormalizeLine1) {
        address = { ...address, addressLines: withNormalizedLine1(address, addressLine2).addressLines };
      }

      dispatch(serviceabilityActions.validateNewApiScrubbedAddress({ address }));
    },
    onError: () => {
      dispatch(
        appActions.analyticsEvent({
          category: TrackingCategory.SERVICEABILITY_CHECK,
          action: TrackingActions.ERROR,
          params: undefined,
        }),
      );
      dispatch(appActions.setErrorRetryAction({ type: serviceabilityActions.scrubServiceAddress.type, payload: null }));
      dispatch(appActions.setModalVisible(Modals.ERROR));
    },
  });

  const scrubAddress = async ({ address }: ScrubAddressInput) => {
    dispatch(serviceabilityActions.setIsServiceabilityLoading(true));

    try {
      await scrubAddressAsync(address);
    } catch (err) {
      throw new Error(`Error scrubbing address: ${JSON.stringify(err)}`);
    } finally {
      dispatch(serviceabilityActions.setIsServiceabilityLoading(false));
    }
  };

  return { data: scrubAddressData as T | null, loading, scrubAddress };
}
